/**
 * HTTP Services to interact with the Hydra API
 * @since 3/28/19
 * @file
 */

import { hydraAPI } from "@passportinc/portal-request";
import _ from "lodash";
import fp from "lodash/fp";
import constants from "../constants";

const { CART_TYPES } = constants;

const hydraAPIGet = hydraAPI.get.bind(hydraAPI);

/**
 * Returns permits by searching on address.
 * @param {string} term - The term to search for.
 * @returns {Promise<Object>} The response from Hydra API.
 */
export const getAddresses = (term) => hydraAPIGet(`/permits/addresses?query=${term}`);

/**
 * Gets a document via id from the hydra API
 * @param {string} id - The id of the file.
 * @param {string} redirect - redirect query parameter.
 * @returns {Promise<Object>} The response from Hydra API.
 */
export const getFile = (id, redirect) => hydraAPIGet(`/permits/files/${id}?${redirect ? "redirect=false" : ""}`);

/**
 * Gets a list of available permits for the current operator.
 * @returns {Promise<Object>} The response from Hydra API.
 */
export const getUserPermits = _.partial(hydraAPIGet, "/permits/permits");

/**
 * Gets a list of citations keyed by license plate number for the current operator.
 * @returns {Promise<Object>} The response from Hydra API.
 */
export const getUserCitations = _.partial(hydraAPIGet, "/permits/citations");
export const getVehicleCitations = (lpn, state, typeId) =>
	hydraAPIGet(`/permits/citations/vehicle?lpn=${lpn}&state=${state}&typeId=${typeId}`);

/**
 * Gets a list of the current user's permit applications.
 * Uses permit-applications dynomite $in operator for multiple statuses: status=$in(["pending","processing","failed"]).
 * @param {Object} [params] - Optional query params.
 * @param {string[]} [params.statuses] - Statuses to filter by (sent as status=$in(JSON array) for backend).
 * @returns {Promise<Object>} The response from Hydra API.
 */
export const getUserApplications = (params = {}) => {
	const query = { eager_fetch: "true" };
	if (Array.isArray(params.statuses) && params.statuses.length) {
		query.status = `$in(${JSON.stringify(params.statuses)})`;
	}
	const search = new URLSearchParams(query).toString();
	return hydraAPIGet(`/permits/applications?${search}`);
};

/**
 * Gets a list of all of the available (visible) permit types for the current operator.
 * @returns {Promise<Object>} The response from Hydra API.
 */
export const getPermitTypesByOperatorId = _.partial(hydraAPIGet, "/permits/eligible-permits");

/**
 * Gets the permit with the given `permitID`.
 * @param {string} permitID - The id of the permit to fetch.
 * @returns {Promise<Object>} The response from Hydra API.
 */
export const getSinglePermit = (permitID) => hydraAPI.get(`/permits/permits/${permitID}`);

/**
 * Gets a limit (waitlist) by id. Returns limit with waitlist array so client can compute position.
 * @param {string} limitId - The limit/waitlist id (permit.waitlist_id).
 * @returns {Promise<Object>} The limit object (includes waitlist array).
 */
export const getLimitById = (limitId) => hydraAPI.get(`/permits/permits/limits/${limitId}`);

/**
 * Gets the list of linkable parent permits for the given child permit.
 * @param {string} permitId - The id of the child permit.
 * @returns {Promise<Object>} Response { data: [{ id, type_id, type_name, status, start_date, end_date, vehicles }] }.
 */
export const getParentPermits = (permitId) => hydraAPI.get(`/permits/permits/${permitId}/parent-permits`);

/**
 * Gets the requesting user's permits that are eligible to be linked as children
 * to a new parent of the supplied type. Used by the application flow's "Linked
 * Children" section to surface orphan children for opt-in linking.
 * @param {string} parentTypeId - The id of the parent permit type the user is applying for.
 * @param {string} [lpn] - Optional vehicle plate. The server requires both `lpn` and `state`
 *                         to filter LPN-required candidates; if either is missing,
 *                         LPN-required candidates are dropped.
 * @param {string} [state] - Optional vehicle state to pair with `lpn`.
 * @returns {Promise<Object>} Response { data: [{ id, type_id, type_name, status, start_date, end_date, vehicles }] }.
 */
export const getLinkableChildren = (parentTypeId, lpn, state) =>
	hydraAPI.get("/permits/linkable-children", {
		params: { type_id: parentTypeId, ...(lpn ? { lpn } : {}), ...(state ? { state } : {}) },
	});

/**
 * Fetches the pending parent permit (if any) currently holding this child's id
 * in its pending_child_ids array. Used by the blue "Parent Permit Awaiting
 * Approval" banner on the CMR dashboard.
 * @param {string} permitId - The id of the child permit.
 * @returns {Promise<Object>} Response { data: { pending_parent: Object | null } }.
 */
export const getPendingParent = (permitId) => hydraAPI.get(`/permits/permits/${permitId}/pending-parent`);

/**
 * Gets the permit types that contain the provided permission external id
 * @param {string} externalId - The external_id of the permission.
 * @returns {Promise<Object>} The response from Hydra API.
 */
export const getTypesByPermissionExternalId = (externalId) => hydraAPIGet(`/permits/permissions/${externalId}/types`);

/**
 * Gets the quote for an item. The per-item `rate` consumers read is native to
 * the quote response; there is no hydration option (the API never read one).
 * @param {Object} body - Request body.
 * @returns {Promise<Object>} The response from Hydra API.
 */
export const getQuote = (body) => hydraAPI.post("/permits/orders/quote", body);

/**
 * Gets the permit with the given `linkCode`.
 * @param {string} linkCode - The link code of the permit to fetch.
 * @returns {Promise<Object>} The response from Hydra API.
 */
export const linkUserToPermit = (linkCode) => hydraAPI.get(`/permits/permit-link/${linkCode}`);

/**
 * Gets the permit-type with the given `typeID`.
 * @param {string} typeID - The id of the permit-type to fetch.
 * @param {string} addressId - The id of the address to limit the permits to.
 * @returns {Promise<Object>} The response from Hydra API.
 */
export const getSinglePermitTypeById = (typeID, addressId) =>
	hydraAPI.get(`/permits/eligible-permits/${typeID}${addressId ? `?address_id=${addressId}` : ""}`);

/**
 * Returns application IDs for other permit applications in the cart.
 * @param {Array<object>} cartItems - Cart items from Redux or quoted checkout items.
 * @param {string} [excludeId] - Application ID to omit (the item being checked).
 * @returns {Array<string>} Sibling application IDs for cart-aware eligibility checks.
 */
export const getCartApplicationIds = (cartItems, excludeId) =>
	_.without(_.compact(cartItems.map((item) => item.type === CART_TYPES.application && item.id)), excludeId);

/**
 * Check if the user can apply for this permit type.
 * @param {string} typeID - The id of the permit-type to fetch.
 * @param {string} applicationID - The id of the application to check.
 * @param {string[]} cartIds - The ids of the applications in the cart to check against.
 * @returns {Promise<Object>} The response from Hydra API.
 */
export const checkPermitApplication = (typeID, applicationID, cartIds = []) =>
	hydraAPI.post(`/permits/eligible-permits/${typeID}/check`, {
		application_id: applicationID,
		...(cartIds.length ? { cart_ids: cartIds } : {}),
	});

export const checkPermitRenewal = (permitId, typeId) =>
	hydraAPI.post(`/permits/eligible-permits/${permitId}/check-permit`, { type_id: typeId });

/**
 * Mapper object that defines eligibility check functions for each cart item type.
 * Each function contains the minimal data setup and API call for that specific cart item type.
 * @type {Object<string, Function>}
 */
export const cartTypeEligibilityMapper = {
	[CART_TYPES.application]: async ({ item, items }) => {
		const { data } = await checkPermitApplication(item.type_id, item.id, getCartApplicationIds(items, item.id));
		return data;
	},
	[CART_TYPES.permit]: async ({ item }) => {
		const { data } = await checkPermitRenewal(item.id, item.type_id);
		return data;
	},
};

/**
 * Runs each cart item's own eligibility check against the given cart snapshot.
 * Pure data fetch — no toasts, no cart mutation, no navigation.
 * @param {Object[]} items - cart items to check, each checked against the full set
 * @returns {Promise<Object[]>} - per-item eligibility results shaped { ...data, item }
 */
export const checkCartEligibility = async (items) =>
	Promise.map(items, async (item) => {
		if (item.type_id && item.id && item.type) {
			const eligibilityFn = cartTypeEligibilityMapper[item.type];
			if (eligibilityFn) {
				const data = await eligibilityFn({ item, items });
				return { ...data, item };
			}
		}
		return null;
	});

export const checkPermitEligibilityThirdParty = (permitId, typeId, vehicle, options = {}) =>
	hydraAPI.post(`/permits/eligible-permits/${permitId}/check-permit`, {
		type_id: typeId,
		third_party_lpn_check_only: true,
		source: "hydra",
		...(vehicle ? { vehicle } : {}),
		...(options.persistResults ? { persist_results: true } : {}),
	});

export const checkAutoRenewal = (permitId, typeId) =>
	hydraAPI.post(`/permits/eligible-permits/${permitId}/check-auto-renew`, { type_id: typeId });

export const checkManualRenewal = (permitId, typeId) =>
	hydraAPI.post(`/permits/eligible-permits/${permitId}/check-manual-renew`, { type_id: typeId });

/**
 * Check if the user already has a permit lined to his account
 * @param {string} lpn - vehicle lpn
 * @param {string} operatorId - The id of the application to check.
 * @param {string} typeId - The id of the permit-type to fetch.
 * @param {string} permissionId - Permission id
 * @param {string} cycleId cycle id
 * @returns {Promise<Object>} The response from Hydra API.
 */
export const checkDuplicatePermit = (lpn, operatorId, typeId, permissionId, cycleId) =>
	hydraAPI.get(
		`/permits/search?query=${lpn}&permission_id=${permissionId}&type_id=${typeId}&cycle_id=${cycleId}&operator_id=${operatorId}`,
	);

/**
 * Gets a user's permit application by application id.
 * @param {string} applicationID - The id of the application to fetch.
 * @returns {Promise<Object>} The response from Hydra API.
 */
export const getApplicationById = (applicationID) =>
	hydraAPI.get(`/permits/applications/${applicationID}?eager_fetch=true`);

/**
 * Saves (creates) an new permit application.
 * @param {Object} applicationDetails - The application data ("body") to update.
 * @returns {Promise<Object>} Resolves with the updated application information.
 */
export const saveApplication = (applicationDetails) =>
	hydraAPI.post(
		"/permits/applications?eager_fetch=true",
		_.pick(applicationDetails, [
			"type_id",
			"cycle_id",
			"start_date",
			"end_date",
			"address_id",
			"permission_id",
			"vehicles",
			"fields",
			"parent_id",
			"pending_child_ids",
		]),
	);

/**
 * Saves (reapply) an new permit application with an existing permit's details.
 * @param {Object} applicationDetails - The application data ("body") to update.
 * @returns {Promise<Object>} Resolves with the updated application information.
 */
export const reapplyApplication = (applicationDetails) =>
	hydraAPI.post(
		"/permits/applications?eager_fetch=true",
		_.pick(applicationDetails, [
			"previous_permit_id",
			"type_id",
			"cycle_id",
			"start_date",
			"address_id",
			"permission_id",
			"fields",
			"vehicles",
			"parent_id",
		]),
	);

/**
 * Deletes a user's application.
 * @param {string} applicationID - The id of the application to delete.
 * @returns {Promise<Object>} The response from Hydra API.
 */
export const deleteApplication = (applicationID) => hydraAPI.delete(`/permits/applications/${applicationID}`);

/**
 * Updates an existing permit application.
 * @param {string} applicationID - The id of the permit application to update.
 * @param {Object} applicationDetails - The application data ("body") to update.
 * @returns {Promise<Object>} Resolves with the updated application information.
 */
export const updateApplication = (applicationID, applicationDetails) =>
	hydraAPI.patch(
		`/permits/applications/${applicationID}?eager_fetch=true`,
		_.pick(applicationDetails, [
			"fields",
			"type_id",
			"visitor",
			"cycle_id",
			"vehicles",
			"documents",
			"start_date",
			"permission_id",
			"pending_child_ids",
			// Must be listed explicitly: `_.pick` is a closed allowlist, so an unlisted key is
			// dropped silently in the browser. Without this entry the auto-renew intent never
			// reaches the application row, and every downstream derivation (permit-applications
			// persistence, orders' card-order forwarding, permits-core's field derivation) is
			// dead code that no component- or handler-level test would catch.
			"auto_renew",
		]),
	);

/**
 * Updates a single permit.
 * @param {string} permitId - The permit object id
 * @param {Object} updates - Object containing updates for the permit
 * @returns {Promise<Object>} - The updated permit information
 */
export const updateSinglePermit = (permitId, updates) => hydraAPI.patch(`/permits/permits/${permitId}`, updates);

/**
 * Patches a child permit with a parent_id to link it to an active/issued parent.
 * @param {string} childId - The child permit id.
 * @param {string} parentId - The parent permit id to link to.
 * @returns {Promise<Object>} The updated child permit information.
 */
export const patchPermitParent = (childId, parentId) =>
	hydraAPI.patch(`/permits/permits/${childId}`, { parent_id: parentId });

/**
 * Atomically appends a child permit id to a pending parent's `pending_child_ids`.
 * Idempotent — calling twice is a no-op. Resolved by the backend on parent activation.
 *
 * @param {string} parentId - The pending parent permit id.
 * @param {string} childId - The child permit id to append.
 * @returns {Promise<Object>} Response { pending_child_ids: string[] }.
 */
export const addPendingChildToParent = (parentId, childId) =>
	hydraAPI.post(`/permits/permits/${parentId}/pending-children`, { child_id: childId });

/**
 * Deletes a single permit.
 * @param {string} permitId - The permit object id
 * @returns {Promise<Object>} - The updated permit information
 */
export const deleteSinglePermit = (permitId) => hydraAPI.post(`/permits/permits/${permitId}/cancel`);

/**
 * Enables auto renew for a single permit.
 * @param {string} permitId - The permit object id
 * @param {Object} updates - Object containing updates for the permit
 * @returns {Promise<Object>} - The updated permit information
 */
export const enableAutoRenew = (permitId, updates) => hydraAPI.post(`/permits/permits/${permitId}/auto-renew`, updates);

/**
 * Enables auto renew for a single permit.
 * @param {string} permitId - The permit object id
 * @returns {Promise<Object>} - The updated permit information
 */
export const disableAutoRenew = (permitId) => hydraAPI.delete(`/permits/permits/${permitId}/auto-renew`);

/*
 * Uploads a single file to the file service
 * @param {Object} fileContents - the contents of the single file being uploaded to the file service
 * @returns {Promise<Object>} - Response object from file service
 */
export const uploadSingleFile = (fileContents) => hydraAPI.post("/permits/files", fileContents);

/**
 * Gets all of the cart information for the drawer / checkout page.
 * @param {Array<{ id, type }>} items - The items currently in the cart.
 * @returns {Promise<Object>} - Response object from the cart API.
 */
export const getCartInformation = (items, paymentType) =>
	hydraAPI.post("/permits/cart", {
		items,
		// forward selected payment_type so the BFF/orders/rates can filter
		// out fees whose `conditions.payment_methods` doesn't include it.
		// Omitted when no method is selected — backend treats a missing
		// `payment_type` as "applies to both methods" (legacy behavior).
		...(paymentType && { payment_type: paymentType }),
	});

/**
 * Will get the permit receipt for the particular order.
 * @param {String} permitId The id of the permit.
 * @param {String} orderId The id of the order for which to get the receipt.
 * @returns {Promise<Object>} The promise to return the response from the api. response: {link: 'http://link'}
 */
export const getPermitReceipt = (permitId, orderId) =>
	hydraAPI.post(`/permits/permits/${permitId}/view-receipt`, {
		order_id: orderId,
	});

/**
 * Will get the permit receipt for the particular order.
 * @param {String} permitId The id of the permit.
 * @returns {Promise<Object>} The promise to return the response from the api. response: {link: 'http://link'}
 */
export const getPermitBarcode = (permitId) => hydraAPI.post(`/permits/permits/${permitId}/view-barcode`, {});

/**
 * getStates Api call to get states list for any vehicle
 * @returns {Promise<Object>} The promise to return the response from the api.
 */
export const getStates = (countries) => hydraAPI.get(`/permits/states?countries=${countries}`);

/**
 * Adds a new vehicle to a permit
 * @param {string} permitId - The permit object id
 * @param {Object} vehicle - Object containing vehicle data
 * @returns {Promise<Object>} The promise to return the response from the api.
 */
export const vehicleAdd = (permitId, vehicle) => hydraAPI.post(`/permits/permits/${permitId}/vehicles`, vehicle);

/**
 * Updates a permit's vehicle
 * @param {string} permitId - The permit object id
 * @param {string} vehicleId - The vehicle object id
 * @param {Object} vehicle - Object containing vehicle data
 * @returns {Promise<Object>} The promise to return the response from the api.
 */
export const vehicleUpdate = (permitId, vehicleId, vehicle) =>
	hydraAPI.patch(`/permits/permits/${permitId}/vehicles/${vehicleId}`, vehicle);

/**
 * Removes a vehicle from a permit
 * @param {string} permitId - The permit object id
 * @param {string} vehicleId - The vehicle object id
 * @param {string} accountId - The account id of the permit holder
 * @returns {Promise<Object>} The promise to return the response from the api.
 */
export const vehicleRemove = (permitId, vehicleId, accountId) =>
	hydraAPI.delete(`/permits/permits/${permitId}/vehicles/${vehicleId}?account_id=${accountId}`, {});

export const assignPass = (permitId, fields) => hydraAPI.post(`/permits/permits/${permitId}/passes`, fields);

/**
 * @param {string[]} ids - Permit ids (bulk-capped server-side)
 * @param {string} [scope] - "basic" | "eligibility" | "full"
 * @param {string[]} [include] - Determination keys to add on top of the scope
 * @returns {Promise<Object>} { results: [{ id, determinations, ... }] }
 */
export const getPermitSSOTState = (ids, scope = "basic", include) =>
	hydraAPI.post("/permits/state", { ids, scope, ...(include && { include }) }).then(fp.get("data"));

/**
 * @param {string[]} permitIds - Permit ids
 * @returns {Promise<Array<Object>>} Per-permit `{ id, determinations }` (or `{ id, error }`) carrying `can_renew`
 *   and `can_reapply`
 */
export const getRenewalStates = (permitIds) =>
	getPermitSSOTState(permitIds, "basic", ["can_renew", "can_reapply"]).then((envelope) =>
		Array.isArray(envelope?.results) ? envelope.results : [],
	);

/**
 * Whether a renewal draft's previous permit refuses it. A permit refused only for its renewal limit stays open
 * unless it cannot be reapplied for either, since eligibility waives the limit when the type allows reapply.
 * @param {Object} state - One entry from `getRenewalStates`.
 * @returns {boolean} True only for a definite refusal.
 */
export const isRenewalClosed = (state) => {
	const { can_renew: canRenew, can_reapply: canReapply } = state?.determinations ?? {};
	if (canRenew?.value !== false) return false;

	return canRenew.meta?.check_reason !== "Max renewal limit reached" || canReapply?.value === false;
};

export const deactivatePass = (permitId, passId) => hydraAPI.delete(`/permits/permits/${permitId}/passes/${passId}`);

/**
 * Cancels a scheduled (not yet started) pass: the redemption returns to the booklet, the pass record is retained.
 *
 * @param {string} permitId - The permit (booklet) id
 * @param {string} passId - The pass (redemption) id
 * @returns {Promise<Object>} The updated permit from the api.
 */
export const cancelPass = (permitId, passId) => hydraAPI.post(`/permits/permits/${permitId}/passes/${passId}/cancel`);

/**
 * Edits the vehicle on an issued or active guest pass. Only plate and state are writable; confirm_duplicate re-submits a rejected duplicate.
 * @param {string} permitId - The permit (booklet) ID
 * @param {string} passId - The redemption (pass) ID
 * @param {Object} body - { fields: { vehicle_plate, vehicle_state }, confirm_duplicate? }
 * @returns {Promise} The updated permit response
 */
export const updatePassVehicle = (permitId, passId, body) =>
	hydraAPI.patch(`/permits/permits/${permitId}/passes/${passId}`, body);

export const getOperatorSettings = () => hydraAPI.get("/permits/operator/settings").then(fp.get("data"));

export const getModels = (make) => hydraAPI.get(`/permits/vehicle-data/models/${make}`).then(fp.get("data"));

export const getPermitChildrenByParentId = (id) => hydraAPI.get(`/permits/permits/${id}/children`).then(fp.get("data"));

export const getChildTypesByParentId = (parentTypeId) =>
	hydraAPI.get("/permits/types/search", { params: { parent_type: parentTypeId } }).then(fp.get("data.data"));

export const getLimitsAtCapacity = (applicationDetails) =>
	hydraAPI.post(
		"/permits/permits/limits/search",
		_.pick(applicationDetails, ["type_id", "cycle_id", "permission_id", "operator_id", "start_date"]),
	);

export const printPhysicalPermit = (id, key) =>
	hydraAPI.get(`/permits/permits/${id}/physical-permit/${key}`).then(fp.get("data"));

/**
 * Search permits via Elasticsearch (POST /permits/search/bulk-permits).
 * Operator/source are added by the API. Response: { total, data, last_sort_value? } or { total } when get_count_only.
 *
 * @param {Object} body - Request body (size, sort, search_after, type_ids, statuses, query, get_count_only, get_ids_only, etc.)
 * @returns {Promise<Object>} Response with total, data, and optionally last_sort_value
 */
export const searchPermits = (body) => hydraAPI.post("/permits/search/bulk-permits", body);

/**
 * Runs an on-demand plate check (PER-4214). POST rather than GET so plates stay
 * out of URL logs; the response is deliberately just `{ permitted }` so no permit
 * or holder data leaks to the caller.
 *
 * @param {string} lpn - License plate number
 * @param {string} vehicleState - Plate state
 * @param {string} permissionId - Permission to check the plate against
 * @returns {Promise<Object>} The response from Hydra API, body `{ permitted }`
 */
export const plateCheck = (lpn, vehicleState, permissionId) =>
	hydraAPI.post("/permits/plate-check", {
		lpn,
		vehicle_state: vehicleState,
		permission_id: permissionId,
	});
